import {Injector} from '@angular/core';
import {AsyncSubject, concat, defer, EMPTY, merge, NEVER, Observable, of, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, share, switchMap, takeUntil, tap} from 'rxjs/operators';
import {hook} from '../../common/rxjs/hook';
import {toNever} from '../../common/rxjs/to-never';
import {AccountService} from '../../components/accounts';
import {WIDGET_DESCRIPTION} from '../../components/layout/widget/widget-description';
import {
    WidgetImplementation,
    WidgetImplementationEvent,
    WidgetImplementationEventFlags,
    WidgetImplementationEventType,
    WidgetInitializedEvent,
    WidgetSettingsUpdatedEvent
} from '../../components/layout/widget/widget-implementation';
import {WidgetSettings} from '../../components/layout/widget/widget-settings';
import {
    getModelAccountFixBySecurity,
    getModelSecurityKeyFix
} from '../../components/model-corrections/correction-common-fixes';
import {SecurityService} from '../../components/security';
import {ListFilterFactory} from '../../factories/list-filter.factory';
import {ExchangeAccount, ExchangeAccountBase, isExchangeAccountBaseEqual} from '../../models/exchange-account';
import {FilterCriterion} from '../../models/filters/filter-criterion';
import {Security} from '../../models/security';
import {clearSecurityKey, isSecurityKeysEqual, SecurityKey} from '../../models/security-key';
import {
    createWHeaderTitlePartSymbol,
    createWHeaderTitlePartText,
    W_HEADER_TITLE_PART_SYMBOL_TYPE,
    WHeaderTitlePartSymbol,
    WidgetHeaderTitlePart
} from '../../pages/dashboard/layout/widget/widget-header/widget-header-title';

/**
 * Interface for the common widget implementation class which can emit into event$ and ready$ streams directly.
 */
interface WidgetImplWithEventEmitters<Settings extends WidgetSettings = WidgetSettings>
    extends WidgetImplementation<Settings> {

    event$: Subject<WidgetImplementationEvent>;
    ready$: Subject<boolean>;
}

export interface CommonWidgetImpl<Settings extends WidgetSettings = WidgetSettings>
    extends WidgetImplementation<Settings> {

    resolveSettings?: (settings: Partial<Settings>) => Observable<Partial<Settings>>;
}

export interface CommonWidgetImplWithSecurityService<Settings extends WidgetSettings = WidgetSettings>
    extends CommonWidgetImpl<Settings> {

    securityService: SecurityService;
}

export interface CommonWidgetImplWithAccountService<Settings extends WidgetSettings = WidgetSettings>
    extends CommonWidgetImpl<Settings> {

    accountService: AccountService;
}

export interface CommonWidgetImplWithSettingsWithUpdates<Settings extends WidgetSettings = WidgetSettings>
    extends CommonWidgetImpl<Settings> {

    settingsWithUpdates$: Observable<Partial<Settings>>;
}

export interface CommonWidgetImplWithInjector<Settings extends WidgetSettings = WidgetSettings>
    extends CommonWidgetImpl<Settings> {

    injector: Injector;
}

export interface CommonWidgetImplWithDestroy<Settings extends WidgetSettings = WidgetSettings>
    extends CommonWidgetImpl<Settings> {

    destroy$?: Observable<void>;
}

export interface WidgetSettingsWithSecurityKey extends WidgetSettings {
    securityKey: SecurityKey;
}

export interface WidgetSettingsWithAccount extends WidgetSettings {
    account: ExchangeAccountBase;
}

export type WidgetSettingsWithSecurityAndAccount = WidgetSettingsWithSecurityKey & WidgetSettingsWithAccount;

const resolveWidgetInitDeps = (widgetImpl: CommonWidgetImpl<any>): Observable<never> =>
    widgetImpl.initialDependency$
        ? widgetImpl.initialDependency$.pipe(
            toNever()
        )
        : EMPTY;

/**
 * Common logic for the initial widget settings setter.
 * Sets the widget implementation's settings and emits the settings relative events.
 * @param widgetImpl
 * @param {(S extends WidgetSettings)} settings
 * @param {WidgetImplementationEventFlags} flags
 * @returns {Observable<S extends WidgetSettings>}
 */
export const setWidgetSettings = <S extends WidgetSettings>(
    widgetImpl: CommonWidgetImpl<S> & WidgetImplWithEventEmitters<S>,
    settings: S,
    flags?: WidgetImplementationEventFlags
): Observable<S> =>
    concat(
        resolveWidgetInitDeps(widgetImpl),
        widgetImpl.settingsService.getDefaultSettings().pipe(
            map(defaults => ({
                ...defaults,
                ...((settings as any) || {}) // The type casting to any is required as a workaround for TS2698 error.
            })),
            switchMap(currentSettings => widgetImpl.resolveSettings
                ? widgetImpl.resolveSettings(currentSettings).pipe(
                    map(resolvedSettings => resolvedSettings
                        ? {...currentSettings, ...resolvedSettings}
                        : currentSettings
                    )
                )
                : of(currentSettings)
            ),
            tap(finalSettings => {
                widgetImpl.settings = finalSettings;

                widgetImpl.isInitialized = true;

                widgetImpl.event$.next({type: WidgetImplementationEventType.INIT} as WidgetInitializedEvent);
                widgetImpl.event$.next({
                    type:    WidgetImplementationEventType.SETTINGS_UPDATED,
                    payload: widgetImpl.settings,
                    flags
                } as WidgetSettingsUpdatedEvent);

                widgetImpl.ready$.next(true);
            }),

            /**
             * [HED-282] Prevent saving of not initialized settings:
             * if the widget is in the process of applying its initial settings,
             * then we should store not yet processed settings in the layout
             * instead of empty/default settings.
             */
            hook({
                subscribe:   () => widgetImpl.pendingSettings = settings,
                unsubscribe: () => widgetImpl.pendingSettings = undefined
            })
        )
    );

/**
 * Common logic for widget settings updating.
 * Updates the widget implementation's settings and emits the settings relative events.
 * @param widgetImpl
 * @param {Partial<S extends WidgetSettings>} updates
 * @param {WidgetImplementationEventFlags} flags
 * @returns {Observable<S extends WidgetSettings>}
 */
export const updateWidgetSettings = <S extends WidgetSettings>(
    widgetImpl: CommonWidgetImpl<S> & WidgetImplWithEventEmitters<S>,
    updates?: Partial<S>,
    flags?: WidgetImplementationEventFlags
): Observable<Partial<S>> => {
    const finalUpdates$ = widgetImpl.resolveSettings

        // Wrap in defer, since it's the most desirable behaviour.
        ? defer(() => widgetImpl.resolveSettings(updates))

        : of(updates);

    return finalUpdates$.pipe(
        filter(finalUpdates => !!finalUpdates),
        tap(finalUpdates => {
            widgetImpl.settings = {
                ...widgetImpl.settings,
                ...(finalUpdates || {})
            };

            widgetImpl.event$.next({
                type: WidgetImplementationEventType.SETTINGS_UPDATED,
                payload: finalUpdates,
                flags
            } as WidgetSettingsUpdatedEvent);
        })
    );
};

/**
 * Returns stream of the widget's settings with the future updates.
 * @returns {Observable<Partial<S extends WidgetSettings>>}
 */
export const getWidgetSettingsWithUpdatesStream = <S extends WidgetSettings>(
    widgetImpl: WidgetImplementation<S>
): Observable<Partial<S>> => {
    const currentSettings$: Observable<S> = defer(() => of(widgetImpl.settings as S));
    const settingsUpdates$: Observable<Partial<S>> = widgetImpl.event$.pipe(
        filter((event: WidgetImplementationEvent): event is WidgetSettingsUpdatedEvent =>
            event.type === WidgetImplementationEventType.SETTINGS_UPDATED
        ),
        map((event: WidgetSettingsUpdatedEvent) => event.payload as Partial<S>),
        share()
    );
    const settingsWithUpdates$ = merge(
        currentSettings$,
        settingsUpdates$
    );

    return widgetImpl.ready$.pipe(
        switchMap(isReady => isReady
            ? settingsWithUpdates$
            : NEVER
        )
    );
};

/**
 * Returns true if SecurityKey updated
 * @returns {boolean}
 */
export const isWidgetSettingsUpdatesSecurityKey = <S extends WidgetSettingsWithSecurityKey>(
    widgetImpl: CommonWidgetImpl<S>,
    settings: Partial<S>
): boolean => {
    const currentSecurityKey: SecurityKey = widgetImpl.settings && widgetImpl.settings.securityKey;

    return !!settings.securityKey
        && !isSecurityKeysEqual(settings.securityKey, currentSecurityKey);
};

/**
 * Returns true if SecurityKey updated and SecurityExchange updated too
 * @returns {boolean}
 */
export const isWidgetSettingsUpdatesSecurityKeyIncludeExchange = <S extends WidgetSettingsWithSecurityKey>(
    widgetImpl: CommonWidgetImpl<S>,
    settings: Partial<S>
): boolean => {
    const currentSecurityKey = widgetImpl.settings && widgetImpl.settings.securityKey;

    return isWidgetSettingsUpdatesSecurityKey(widgetImpl, settings)
        && (
            !currentSecurityKey
            || currentSecurityKey.securityExchange !== settings.securityKey.securityExchange
        );
};

/**
 * Returns stream of the widget's settings with correct SecurityKey.
 * @returns {Observable<Partial<S extends WidgetSettingsWithSecurityKey>>}
 */
export const getWidgetSettingsWithCorrectSecurityKey = <S extends WidgetSettingsWithSecurityKey>(
    widgetImpl: CommonWidgetImplWithSecurityService<S>,
    settings: Partial<S>
): Observable<Partial<S>> =>
    getModelSecurityKeyFix(settings, widgetImpl.settings, widgetImpl) as Observable<Partial<S>>;

export const getFilteredSecurityKeyCorrectionBySettings = <S extends WidgetSettingsWithSecurityKey>(
    widgetImpl: CommonWidgetImplWithSecurityService<S>,
    settings: Partial<S>,
    params: {
        securityPredicate: (security: Security) => boolean;
        securityFilterCriteria: FilterCriterion[];
    }
): Observable<Partial<S> | undefined> => {
    const isSecurityChanged = isWidgetSettingsUpdatesSecurityKey(widgetImpl, settings);

    const fetchByFilter = (): Observable<Security> => {
        const listFilter = ListFilterFactory.createWithSearchFields(params.securityFilterCriteria);

        return widgetImpl.securityService.getSecurities(listFilter).pipe(
            map(listResponse => listResponse.data[0])
        );
    };

    const getSecurity = (): Observable<Security> =>
        (
            settings.securityKey
                ? widgetImpl.securityService.getSecurityBySecurityKey(settings.securityKey)
                : of(undefined)
        ).pipe(
            switchMap(settingsSecurity =>
                settingsSecurity && params.securityPredicate(settingsSecurity)
                    ? of(settingsSecurity)
                    : fetchByFilter()
            )
        );

    return isSecurityChanged
        ? getSecurity().pipe(
            map(security =>
                isSecurityKeysEqual(settings.securityKey, security)
                    ? undefined
                    : {securityKey: clearSecurityKey(security)} as Partial<S>
            )
        )
        : of(undefined);
};

export const widgetImplDestroy$ = (
    widgetImpl: WidgetImplementation
): Observable<void> => {
    const subject = new AsyncSubject<void>();
    const originalDestroy = widgetImpl.destroy;

    widgetImpl.destroy = () => {
        if (originalDestroy) {
            originalDestroy.call(widgetImpl);
        }

        subject.next();
        subject.complete(); // It's required due to nature of AsyncSubject.
    };

    return subject.asObservable();
};

export const listenToWidgetSettingsSecurityInvalidation = <S extends WidgetSettingsWithSecurityKey>(
    widgetImpl: CommonWidgetImplWithSecurityService<S>
        & CommonWidgetImplWithDestroy<S>
        & CommonWidgetImplWithSettingsWithUpdates<S>
): void => {
    widgetImpl.destroy$ = widgetImpl.destroy$ || widgetImplDestroy$(widgetImpl);
    widgetImpl.settingsWithUpdates$ = widgetImpl.settingsWithUpdates$
        || getWidgetSettingsWithUpdatesStream(widgetImpl);

    widgetImpl.settingsWithUpdates$.pipe(
        map(({securityKey}) => securityKey),
        filter(securityKey => !!securityKey),
        distinctUntilChanged(isSecurityKeysEqual),
        switchMap(securityKey => widgetImpl.securityService.getInvalidationStreamBySecurityKey(securityKey)),
        switchMap(() => {
            const {securityKey} = widgetImpl.settings;

            // Bypass same-securityKey checking.
            widgetImpl.settings.securityKey = null;

            return widgetImpl.updateSettings({securityKey} as Partial<S>);
        }),
        takeUntil(widgetImpl.destroy$)
    ).subscribe();
};

export const getAccountCorrectionBySettingsWithSecurity = <S extends WidgetSettingsWithSecurityAndAccount>(
    widgetImpl: CommonWidgetImplWithAccountService<S> & CommonWidgetImplWithSecurityService<S>,
    settings: Partial<S>
): Observable<Partial<S>> =>
    getModelAccountFixBySecurity(settings, widgetImpl.settings, widgetImpl) as Observable<Partial<S>>;

export const getFilteredAccountCorrectionBySettings = <S extends WidgetSettingsWithAccount>(
    widgetImpl: CommonWidgetImplWithAccountService<S>,
    settings: Partial<S>,
    params: {
        accountPredicate: (account: ExchangeAccount) => boolean;
    }
): Observable<Partial<S>> => {
    const origAccount = settings.account && widgetImpl.accountService.getAccountByIdFromCache(settings.account.id);
    const isCorrectionNeeded = !(origAccount && params.accountPredicate(origAccount));

    const result: Partial<S> = {};

    if (isCorrectionNeeded) {
        const correctedAccount = widgetImpl.accountService.getAllAccountsFromCache().find(params.accountPredicate);

        result.account = correctedAccount && AccountService.toAccountBase(correctedAccount);
    }

    return of(result);
};

type CorrectionFn<S extends WidgetSettings> = (
    settings: Partial<S>,
    widgetImpl: CommonWidgetImpl<S>
) => Observable<Partial<S>>;

export const pipeSettingsCorrections = <S extends WidgetSettings>(
    correctionFns: Array<CorrectionFn<S>>
): CorrectionFn<S> => {
    const callCorrection = (
        settings: Partial<S>,
        widgetImpl: CommonWidgetImpl<S>,
        index: number
    ): Observable<Partial<S>> =>
        index >= correctionFns.length
            ? of(settings)
            : (
                correctionFns[index](settings, widgetImpl).pipe(
                    switchMap((correction: Partial<S>) =>
                        callCorrection({...settings, ...correction}, widgetImpl, index + 1)
                    )
                )
            );

    return (settings, widgetImpl) => callCorrection(settings, widgetImpl, 0);
};

export const getSettingsUpdateKeysDict = <S extends WidgetSettings>(
    settingsUpdate: Partial<S>
): Partial<{ [key in keyof S]: true }> =>
    (Object.keys(settingsUpdate) as Array<keyof S>)
        .reduce((dict, key) => {
            dict[key] = true;

            return dict;
        }, {} as Partial<{ [key in keyof S]: true }>);

export const hasSettingUpdateField = <S extends WidgetSettings, Field extends keyof S>(
    settingsUpdate: Partial<S>,
    fieldName: Field
): boolean => settingsUpdate.hasOwnProperty(fieldName);

/**
 * Checks if settingsUpdate doesn't contain any of fieldNames,
 * or the values are not equal to the values in settings.
 *
 * @param settings
 * @param settingsUpdate
 * @param fieldNames
 * @returns {boolean}
 */
export const isSettingsUpdateEmptyOrSame = <S extends WidgetSettings>(
    settings: S,
    settingsUpdate: Partial<S>,
    ...fieldNames: Array<keyof S>
): boolean => {
    const check = (fieldName: keyof S) =>
        !hasSettingUpdateField(settingsUpdate, fieldName)
        || settings && settings[fieldName] === settingsUpdate[fieldName];

    return fieldNames.length === 1
        ? check(fieldNames[0])
        : fieldNames.every(check);
};

export const getSettingsWithUpdateField = <S extends WidgetSettings, K extends keyof S>(
    settings: S,
    settingsUpdate: Partial<S>,
    fieldName: K
): S[K] =>
    settingsUpdate && hasSettingUpdateField(settingsUpdate, fieldName)
        ? settingsUpdate[fieldName]
        : settings && settings[fieldName];

export const getSettingsWithUpdateFields = <S extends WidgetSettings, K extends keyof S>(
    settings: S,
    settingsUpdate: Partial<S>,
    fieldNames: K[]
): Pick<S, K> =>
    fieldNames.reduce(
        (dict, fieldName) => {
            dict[fieldName] = getSettingsWithUpdateField(settings, settingsUpdate, fieldName);

            return dict;
        },
        {} as Pick<S, K>
    );

export const getWidgetWithSecurityTitlePartsStream = <S extends WidgetSettingsWithSecurityKey>(
    widgetImpl: CommonWidgetImplWithSettingsWithUpdates<S> & CommonWidgetImplWithInjector<S>
): Observable<WidgetHeaderTitlePart[]> => {
    const {widgetLabel} = widgetImpl.injector.get(WIDGET_DESCRIPTION);

    const getTitleParts = (): WidgetHeaderTitlePart[] => {
        const {title: settingsTitle, securityKey} = widgetImpl.settings || {} as S;

        const titleParts: WidgetHeaderTitlePart[] = [
            createWHeaderTitlePartText(
                settingsTitle || widgetLabel,
                !settingsTitle
            )
        ];

        if (securityKey) {
            titleParts.push(createWHeaderTitlePartSymbol(securityKey));
        }

        return titleParts;
    };

    return widgetImpl.settingsWithUpdates$.pipe(
        filter(settingsUpdate =>
            hasSettingUpdateField(settingsUpdate, 'title')
            || hasSettingUpdateField(settingsUpdate, 'securityKey')
        ),
        map(getTitleParts)
    );
};

/**
 * [TT-247] Handle Smart security (workaround) in Chart
 */
export const getWidgetWithAltSecurityTitlePartsStream = <S extends WidgetSettingsWithSecurityKey>(
    widgetImpl: CommonWidgetImplWithSettingsWithUpdates<S> & CommonWidgetImplWithInjector<S>,
    altSecurityKeyResolver: (securityKey: SecurityKey) => Observable<SecurityKey>
): Observable<WidgetHeaderTitlePart[]> => {
    const resolveAltSymbolPart = (titleParts: WidgetHeaderTitlePart[]): Observable<WidgetHeaderTitlePart[]> => {
        let result$: Observable<WidgetHeaderTitlePart[]>;
        const symbolPart = titleParts.find(part =>
            part.type === W_HEADER_TITLE_PART_SYMBOL_TYPE
        ) as WHeaderTitlePartSymbol;

        if (symbolPart) {
            result$ = altSecurityKeyResolver(symbolPart.data.securityKey).pipe(
                map(altSecurityKey => {
                    symbolPart.data.altSecurityKey = altSecurityKey;

                    return titleParts;
                })
            );
        } else {
            result$ = of(titleParts);
        }

        return result$;
    };

    return getWidgetWithSecurityTitlePartsStream(widgetImpl).pipe(
        switchMap(resolveAltSymbolPart)
    );
};

export const getSettingsSecurityKeyStream = <S extends WidgetSettingsWithSecurityKey>(
    widgetImpl: CommonWidgetImplWithSettingsWithUpdates<S>
): Observable<SecurityKey> =>
    widgetImpl.settingsWithUpdates$.pipe(
        map(settingsUpdates => settingsUpdates.securityKey),
        filter(securityKey => !!securityKey),
        distinctUntilChanged(isSecurityKeysEqual)
    );

export const getSettingsSecurityStream = <S extends WidgetSettingsWithSecurityKey>(
    widgetImpl: CommonWidgetImplWithSecurityService<S>
        & CommonWidgetImplWithSettingsWithUpdates<S>
): Observable<Security> =>
    getSettingsSecurityKeyStream(widgetImpl).pipe(
        switchMap(securityKey => widgetImpl.securityService.getSecurityBySecurityKey(securityKey))
    );

export const getSettingsAccountBaseStream = <S extends WidgetSettingsWithAccount>(
    widgetImpl: CommonWidgetImplWithSecurityService<S>
        & CommonWidgetImplWithSettingsWithUpdates<S>
): Observable<ExchangeAccountBase> =>
    widgetImpl.settingsWithUpdates$.pipe(
        filter(settingsUpdates => hasSettingUpdateField(settingsUpdates, 'account')),
        map(settingsUpdates => settingsUpdates.account),
        distinctUntilChanged(isExchangeAccountBaseEqual)
    );

export const correctEnum = <E extends object, V extends keyof E>(enumObj: E, value: V, defaultValue: V): V =>
    value && Object.hasOwn(enumObj, value)
        ? value
        : defaultValue;
